import { DOCUMENT } from '@pkg/config/sanity/schemas';
import { capitalize } from '@pkg/utilities/strings';
import type { LOCALE_CODE } from '@pkg/config/languages/localeCodes';
import type { PageSeo } from '@pkg/sanity-utils/types/seo';
import { useSanityData } from '@pkg/sanity-utils/composables/useSanityData';
import { SURPRISE_BAG_RESULTS_PER_PAGE } from '@pkg/types/surpriseBag';
import type { BrandProfileSanityDoc, BrandProfileItemsResponse } from '~/app/types/brandProfiles';
import { BRAND_PLACEHOLDER } from '~/app/types/brandProfiles';
import { brandProfileDocumentBySlug } from '~/apis/sanity/queries/brandProfileBySlug';

function normalizeCities(rawCities: unknown, locale: string): string[] {
  if (!Array.isArray(rawCities)) { return []; }
  return (rawCities as unknown[])
    .filter((city): city is string => typeof city === 'string' && city.trim().length > 0)
    .map(city => capitalize(city))
    .sort((a, b) => a.localeCompare(b, locale));
}

function titleFromPartnerSlug(slug: string | undefined): string {
  if (!slug) { return 'Partner'; }
  return slug
    .split('-')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function resolveBrandProfileSeo(
  brandProfile: BrandProfileSanityDoc | null | undefined,
  brandName: string,
): PageSeo {
  const brandSeo = brandProfile?.seoSocial;
  const defaultSeo = brandProfile?.brandProfileDefaultTexts?.defaultSeoSocial;

  const withBrand = (value?: string | null) =>
    value?.split(BRAND_PLACEHOLDER).join(brandName).trim() || undefined;

  return {
    metaTitle: brandSeo?.metaTitle?.trim() || withBrand(defaultSeo?.metaTitle),
    metaDescription: brandSeo?.metaDescription?.trim() || withBrand(defaultSeo?.metaDescription),
    socialImage: brandSeo?.socialImage ?? defaultSeo?.socialImage ?? undefined,
  };
}

export function useBrandProfileCityBags(chainId: string, city: string) {
  if (!chainId || !city) {
    return { data: computed(() => undefined), pending: ref(false), loadMore: () => {}, canLoadMore: ref(false) };
  }

  const PAGE_SIZE = SURPRISE_BAG_RESULTS_PER_PAGE;
  const items = ref<Array<Record<string, unknown>>>([]);
  const pending = ref(true);
  const currentPage = ref(0);
  const canLoadMore = ref(false);

  const fetchPage = async(page: number) => {
    pending.value = true;
    try {
      const response = await $fetch<BrandProfileItemsResponse>(`/api/brand-profile/${encodeURIComponent(chainId)}/items`, {
        method: 'POST',
        body: { paging: { page, size: PAGE_SIZE }, city },
      });

      if (page === 0) {
        items.value = response?.items ?? [];
      } else {
        items.value = items.value.concat(response?.items ?? []);
      }

      const totalElements = response?.paging?.totalElements ?? 0;
      canLoadMore.value = (page + 1) * PAGE_SIZE < totalElements;
    } catch {
      if (page === 0) { items.value = []; }
      canLoadMore.value = false;
    } finally {
      pending.value = false;
    }
  };

  const loadMore = () => {
    if (!canLoadMore.value || pending.value) { return; }
    currentPage.value++;
    fetchPage(currentPage.value);
  };

  fetchPage(0);

  return { data: computed(() => items.value.length > 0 ? items.value : undefined), pending, loadMore, canLoadMore };
}

export async function useBrandProfileData(
  _query: string,
  params?: Record<string, unknown>,
) {
  const partnerSlug = params?.slug as string | undefined;
  const locale = params?.locale as LOCALE_CODE | undefined;
  const title = titleFromPartnerSlug(partnerSlug);

  if (!locale || !partnerSlug) {
    return null;
  }

  const { data: brandProfileSanity } = await useSanityData<BrandProfileSanityDoc | null>(
    brandProfileDocumentBySlug,
    { locale, slug: partnerSlug.trim() },
  );

  const chainIdParam = (() => {
    const raw = brandProfileSanity.value?.chainId;
    if (raw == null) { return undefined; }
    const trimmed = String(raw).trim();
    return trimmed.length > 0 ? trimmed : undefined;
  })();

  const { data: brandProfileMarketing } = chainIdParam
    ? await useFetch(`/api/brand-profile/${encodeURIComponent(chainIdParam)}`)
    : { data: ref(null) };

  if (!brandProfileMarketing.value) {
    return null;
  }

  const brandName = brandProfileSanity.value?.name?.trim() || title;
  const seoSocial = resolveBrandProfileSeo(brandProfileSanity.value, brandName);
  const cities = normalizeCities((brandProfileMarketing.value as { cities?: unknown })?.cities, locale);

  return {
    data: ref({
      page: {
        seoSocial,
        _type: DOCUMENT.BRAND_PROFILE,
        title,
        brandProfileSanity: brandProfileSanity.value ?? null,
        brandProfileMarketing: brandProfileMarketing.value ?? null,
        cities,
        chainId: chainIdParam ?? null,
      },
    }),
  };
}
